import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Box, TextField, IconButton, CircularProgress, Paper, Typography, Grid, Avatar, Button, Dialog, DialogTitle, DialogContent, TextareaAutosize } from "@mui/material";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import { Sidebar } from "./Sidebar";
import './chat.css';
import { req } from "./ServerUtils";
import { useParams, useNavigate } from "react-router-dom";
import { LinkedinPostPreview } from "./LinkedinPostPreview";


export default function ChatBox() {
  const navigate = useNavigate();
  const { convId } = useParams();
  let [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const controllerRef = useRef(null);
  const chatBoxRef = useRef(null);
  const [responseId, setResponseId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [carousel, setCarousel] = useState(null);
  const [post, setPost] = useState(null);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!convId) return;
    const fetchConversation = async () => {
      try {
        req(`/conversation/${convId}`)
        .then((res) => {
          console.log("conversation", res);
          if (res.carousel) {
            setCarousel(res.carousel);
          }
          if (res.post) {
            setPost(res.post);
          }
          for (let i = res.messages.length - 1; i >= 0; i--) {
            const m = res.messages[i];
            if (m.role === "assistant" && m.response_id) {
              console.log("setting response id", m.response_id);
              setResponseId(m.response_id);
              break;
            }
          }

          // filter out empty assistant messages & system messages
          res.messages = res.messages.filter((m) => {
            return (m.role !== "assistant" || (m.role === "assistant" && m.content?.length > 0)) && (m.role !== "system");
          });

          const msges = res.messages.map((m) => {
            try {
              const j = JSON.parse(m.content);
              for (let k in j) {
                m[k] = j[k].split("\n");
              }
              if (m.role === "assistant" && m.post) {
                setPost(m.post);
              }
            } catch (err) {
              if (m.type == "function_call") {
                if (m.name == "get_post_examples") {
                  m.lines = ["Finding high-engagement post examples..."];
                } else if (m.name == "get_hook_examples") {
                  m.lines = ["Finding a good hook based on top-performing examples..."];
                } else {
                  m.lines = ["Calling function: " + m.name + "(" + m.arguments + ")"];
                }
              } else if (m.output && m.output.length > 0) {
                m.posts = JSON.parse(m.output);
                m.posts = m.posts.map((p) => {
                  return {
                    lines: p.split("\n")
                  }
                });
                console.log("posts", m.posts);
              } else if (m.content) {
                m.lines = m.content.split("\n");
              }
              // console.log(err);
            }
            return m;
          });
          console.log("last: ", msges[msges.length - 1])
          setMessages(msges || []);
          setConversationId(convId);
          
        })
        .catch((err) => {
          console.error("Failed to fetch conversation:", err);
        });
      } catch (err) {
        console.error("Failed to fetch conversation:", err);
      }
    };
    fetchConversation();
  }, [convId]);

  const sendMessage = async (text = input) => {
    if (!text.trim()) return;
    const userMsg = { role: "user", lines: text.split("\n") };
    const assistantMsg = { role: "assistant", lines: [] };
    messages = [...messages, userMsg, assistantMsg];
    setMessages(messages);
    setInput("");
    setIsLoading(true);

    controllerRef.current = new AbortController();
    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem("mmb_token");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch("https://live.makemybrand.ai/chat", {
        method: "POST",
        body: JSON.stringify({ user_msg: text, prev_response_id: responseId, conv_id: conversationId }),
        signal: controllerRef.current.signal,
        credentials: "include",
        headers: headers,
      }).catch((err) => {
        console.log(err);
        throw err;
      });
      if (!res.ok || !res.body) throw new Error("Request failed or no stream");


      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let partial = '';
      let msgText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        partial += decoder.decode(value, { stream: true });

        let lines = partial.split("\n");
        partial = lines.pop(); // hold incomplete line

        console.log("lines", JSON.stringify(lines));

        for (let line of lines) {
          const msg = JSON.parse(line);
          
          if (msg.type === "response_text") {
            msgText += msg.data;
  
            let postWrapper;
            try {
              postWrapper = JSON.parse(msgText);
            } catch (err) {
            }
            if (postWrapper?.intro || postWrapper?.body) {
              assistantMsg.intro = postWrapper.intro?.split("\n");
              assistantMsg.body = postWrapper.body?.split("\n");
              assistantMsg.lines = [];
              setPost(assistantMsg.body);
            } else {
              assistantMsg.lines = msgText.split("\n");
            }
            messages = [...messages.slice(0, -1), assistantMsg];
            setMessages(messages);

          } else if (msg.type === "response_id") {
            console.log("setting response id", msg.data);
            setResponseId(msg.data);
          } else if (msg.type === "error") {
            msgText += "Sorry, there was an error. Please try again later.";
          } else if (msg.type === "conversation_id") {
            setConversationId(msg.data);
            navigate(`/chat/${msg.data}`);
          }
        }
      }

    } catch (err) {
      console.log(err); 
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { role: "assistant", lines: [`Sorry, there was an error. Please try again later.`] },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const abortStream = () => {
    controllerRef.current?.abort();
    setIsLoading(false);
    setMessages((prev) => [
      ...prev.slice(0, -1),
      { role: "assistant", lines: ["(response aborted)"] },
    ]);
  };

  const handleKeyPress = (e) => {
    if (!e.ctrlKey && !e.shiftKey && e.key === "Enter" && !isLoading) {
      sendMessage();
    }
  };

  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const updatePostOnServerPost = () => {
    console.log("updating post on server post", post);
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    setDebounceTimeout(setTimeout(() => {
      req(`/conversation-metadata/${conversationId}`, 'POST', JSON.stringify({
        post: post,
      }));
    }, 3000));
  }
  console.log("messages", messages);


  return (
    <Box display="flex" height="100vh">
      <Sidebar selected="write" />
    
      <Box display="flex" flexDirection="column" mx="auto" p={2} style={{
          borderRight: "1px solid rgb(245, 245, 245)",
          height: "100vh",
          overflow: "auto",
        }}>
        <div>
          {conversationId && (
            <Button variant="outlined" color="primary" style={{}} onClick={() => {
              setMessages([]);
              setInput("");
              setResponseId(null);
              setConversationId(null);
              setCarousel(null);
              setPost(null);
              navigate(`/chat`);
            }}>{"<"} Back</Button>
          )}
        </div>

        <Box ref={chatBoxRef} flexGrow={messages.length === 0 ? 0 : 1} overflow="auto" pr={1} >
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                display: "flex",
                justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                marginBottom: "10px",
              }}
            >
              <Avatar
                src={m.role === "user" ? "/images/avatar.jpg" : "/images/chatbot.jpg"}
                sx={{ width: 40, height: 40, mr: 1 }}
              />
              <Paper sx={{ p: 1.5, maxWidth: "100%", borderRadius: 2,border: "solid 1px #efefef;", boxShadow: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {m.role === "user" ? "You" : "Assistant"}
                </Typography>
                
                {m.intro && <div>render_lines(m.intro)</div>}
                {m.post && <div style={{
                }}>
                  {render_lines(m.post)}
                  {/* <PostActions post={m.post} setCarousel={setCarousel} /> */}
                </div>}

                {m.posts && m.posts.length > 0 &&
                  <div style={{
                    

                    display: 'flex',
                    flexDirection: 'row',
                    gap: 10,
                    overflowX: 'auto',
                    width: '100%',
                  }}>
                    {m.posts.map((post, index) => (
                      <div key={index} style={{
                        border: "1px solid #efefef",
                        borderRadius: 10,
                        padding: 10,
                        marginTop: 10,

                        minWidth: 300,
                        height: 400,
                        overflowY: 'auto',
                      }}>
                        {render_lines(post.lines)}
                      </div>
                    ))}
                  </div>
                }
                
                {m.lines?.length === 0 && isLoading && i === messages.length - 1 ? (
                  <Box sx={{ width: 100, height: 10, borderRadius: 1, background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)", animation: "skeleton-loading 1.5s infinite linear", backgroundSize: "200% 100%" }} />
                ) : (
                  m.lines?.map((line, index) => (
                    <motion.p
                      key={index}
                      style={{ margin: "4px 0", color: line.startsWith("Error:") ? "red" : "inherit" }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.05 }}
                    >
                      {line}
                    </motion.p>
                  ))
                )}
              </Paper>
            </motion.div>
          ))}
        </Box>
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          maxWidth: 600,
          width: 600,
          margin: "0 auto",
          justifyContent: "start",
          flexGrow: messages.length === 0 ? 1 : 0,
        }}>
          <ChatButtons input={input} setInput={setInput} isLoading={isLoading} handleKeyPress={handleKeyPress} sendMessage={sendMessage} abortStream={abortStream} messages={messages} />
          
          {/* {messages.length === 0 && <Suggestions sendMessage={sendMessage} />} */}
          {messages.length === 0 && <RecentConversations />}
        </div>
        
      </Box>

      {post &&
        <Box display="flex" flexDirection="column" flexGrow={1} mx="auto" p={2} style={{
          height: "100%",
        }}>
            <PostPreview post={post} carousel={carousel} setCarousel={setCarousel} conversationId={conversationId} setPost={setPost} updatePostOnServerPost={updatePostOnServerPost}/>       
        </Box>}

      
    </Box>
  );
}

function render_lines(lines) {
  if (!lines) return null;
  return <div style={{
    display: "flex",
    gap: 10,
    height: "100%",
    justifyContent: "start",
    flexDirection: "column",
  }}>{lines.map((line, index) => (
    <div key={index} style={{
      width: "100%",
      lineHeight: 1,
    }}>
      {line}
    </div>
  ))}</div>;
}

function ChatButtons({input, setInput, isLoading, handleKeyPress, sendMessage, abortStream, messages}) {
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (inputRef.current) {

      inputRef.current.focus();
    }
  }, [isLoading]);

  return (
    <Box display="flex" mt={2} mb={2}>
      <TextareaAutosize
        inputRef={inputRef}
        fullWidth
        placeholder={messages.length == 0 ? "Say hi" : "Type a response"}
        value={input}
        minRows={3}
        style={{
          width: "100%",
          padding: 10
        }}
        disabled={isLoading}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyPress}
      />
      <IconButton color="primary" onClick={() => sendMessage()} disabled={isLoading}>
        {isLoading ? <CircularProgress size={20} /> : <KeyboardArrowRightIcon />}
      </IconButton>
      {isLoading && (
        <IconButton onClick={abortStream}>
          <DoDisturbOnIcon />
        </IconButton>
      )}
    </Box>
  )

}


function RecentConversations({sendMessage}) {
  const [conversations, setConversations] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    req('/conversations').then((res) => {
      console.log("Recent conversations", res);
      setConversations(res);
    }).catch((error) => {
      console.log("Error getting recent conversations", error);
    });
  }, []);

  return  (
    <Grid container spacing={2} mt={1}>
      {conversations.map((conv, i) => (
        <Grid item xs={12} sm={6} key={i}>
          <Paper
            sx={{ p: 2, cursor: "pointer" }}
            onClick={() => {
              navigate(`/chat/${conv.conversation_id}`);
            }}
          >
            <Typography variant="subtitle1" color="text.secondary">{conv.title}</Typography>
            <Typography variant="caption" color="text.secondary">{getRecency(conv.created_at)}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>);

  function getRecency(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));
    const diffSeconds = Math.ceil(diffTime / 1000);
    if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    }
    if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    }
    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 30) {
      return `${diffDays}d ago`;
    }

    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
    if (diffMonths < 12) {
      return `${diffMonths}m ago`;
    }
    const diffYears = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 365));
    return `${diffYears}y ago`;
  }
}


function Suggestions({sendMessage}) {
  return  (
    <div style={{
      border: "1px solid rgb(241 241 241)",
      borderRadius: 6,
      padding: 10,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      margin: "0 auto",
      justifyContent: "start",
      width: "100%",
    }}>
      {[
        "What is Ghibli Studio Art?",
        "Embracing failure on LinkedIn",
        "Comments Performance Analysis",
        "Post Performance Analysis Request",
      ].map((text, i) => (
          <div
          key={i}
            style={{
              padding: 10,
              cursor: "pointer",
              fontSize: "0.9rem",
              opacity: 0.8,
            }}
            onClick={() => sendMessage(text)}
          >
            {text}
          </div>
      ))}
    </div>);
}


function PostPreview({post, carousel, setCarousel, conversationId, setPost, updatePostOnServerPost}) {
  return <>
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      // border: "1px solid #ccc",  
      padding: 3,
      borderRadius: 10,
      overflow: "auto",

      flexGrow: 1,
      width: 550
    }}>
      <LinkedinPostPreview text={post.join('\n')} carousel={carousel} setText={t => {
        setPost(t.split('\n'));
        updatePostOnServerPost();
      }} post={post} setCarousel={setCarousel} conversationId={conversationId} />
    </div>
    
  </>
}
